const unitCategories = {
  temperature: {
    native: '°C',
    units: ['°C', '°F'],
    conversions: {
      '°F': {
        toNative: (val) => (val - 32) * 5 / 9,
        fromNative: (val) => val * 9 / 5 + 32
      }
    }
  },
  length: {
    native: 'mm',
    units: ['mm', 'inch', 'km', 'mile'],
    conversions: {
      'inch': {
        toNative: (val) => val * 25.4,
        fromNative: (val) => val / 25.4
      },
      'km': {
        toNative: (val) => val * 1000000,
        fromNative: (val) => val / 1000000
      },
      'mile': {
        toNative: (val) => val * 1609344,
        fromNative: (val) => val / 1609344
      }
    }
  },
  power: {
    native: 'kW',
    units: ['kW', 'hp'],
    conversions: {
      'hp': {
        toNative: (val) => val * 0.7457,
        fromNative: (val) => val / 0.7457
      }
    }
  },
  pressure: {
    native: 'kPa',
    units: ['kPa', 'psi', 'bar'],
    conversions: {
      'psi': {
        toNative: (val) => val * 6.894757,
        fromNative: (val) => val / 6.894757
      },
      'bar': {
        toNative: (val) => val * 100,
        fromNative: (val) => val / 100
      }
    }
  },
  angle: {
    native: 'degrees',
    units: ['degrees'],
    conversions: {}
  },
  torque: {
    native: 'Nm',
    units: ['Nm', 'lb-ft'],
    conversions: {
      'lb-ft': {
        toNative: (val) => val * 1.35582,
        fromNative: (val) => val / 1.35582
      }
    }
  },
  forceRate: {
    native: 'N/mm',
    units: ['N/mm', 'lb/in'],
    conversions: {
      'lb/in': {
        toNative: (val) => val * 0.1751268,
        fromNative: (val) => val / 0.1751268
      }
    }
  },
  mass: {
    native: 'kg',
    units: ['kg', 'lb'],
    conversions: {
      'lb': {
        toNative: (val) => val * 0.453592,
        fromNative: (val) => val / 0.453592
      }
    }
  },
  volume: {
    native: 'L',
    units: ['L', 'gal'],
    conversions: {
      'gal': {
        toNative: (val) => val * 3.78541,
        fromNative: (val) => val / 3.78541
      }
    }
  },
  speed: {
    native: 'm/s',
    units: ['m/s', 'km/h', 'mph', 'ft/s'],
    conversions: {
      'km/h': {
        toNative: (val) => val / 3.6,
        fromNative: (val) => val * 3.6
      },
      'mph': {
        toNative: (val) => val * 0.44704,
        fromNative: (val) => val / 0.44704
      },
      'ft/s': {
        toNative: (val) => val * 0.3048,
        fromNative: (val) => val / 0.3048
      }
    }
  },
  time: {
    native: 's',
    units: ['s', 'min', 'hr'],
    conversions: {
      'min': {
        toNative: (val) => val * 60,
        fromNative: (val) => val / 60
      },
      'hr': {
        toNative: (val) => val * 3600,
        fromNative: (val) => val / 3600
      }
    }
  },
  fuelEfficiency: {
    native: 'L/100km',
    units: ['L/100km', 'mpg'],
    conversions: {
      'mpg': {
        toNative: (val) => 235.215 / val,
        fromNative: (val) => 235.215 / val
      }
    }
  },
  // Units that don't need conversion
  noConversion: {
    native: null,
    units: ['', '%', 'text', 'rpm', 'kWh', 'V', 'A', 'W'],
    conversions: {}
  }
};

// Map unit to category
const unitToCategory = {};
Object.keys(unitCategories).forEach(category => {
  unitCategories[category].units.forEach(unit => {
    unitToCategory[unit] = category;
  });
});

// Function to get category for a unit
export const getCategory = (unit) => unitToCategory[unit] || 'noConversion';

// Function to convert value from display unit to native unit
export const convertToNative = (value, unit) => {
  if (!value || isNaN(value)) return value;
  const category = getCategory(unit);
  if (category === 'noConversion' || unit === unitCategories[category].native) return value;
  const conversion = unitCategories[category].conversions[unit];
  if (conversion) {
    return conversion.toNative(parseFloat(value));
  }
  return value;
};

// Function to convert value from native unit to display unit
export const convertFromNative = (value, unit) => {
  if (!value || isNaN(value)) return value;
  const category = getCategory(unit);
  if (category === 'noConversion' || unit === unitCategories[category].native) return value;
  const conversion = unitCategories[category].conversions[unit];
  if (conversion) {
    return conversion.fromNative(parseFloat(value));
  }
  return value;
};

// Get units for a category
export const getUnitsForCategory = (category) => unitCategories[category]?.units || [];

// Get native unit for a category
export const getNativeUnit = (category) => unitCategories[category]?.native;

// Grouped units for UI
const groupedUnits = [
  {
    label: 'Pressure',
    units: ['kPa', 'psi', 'bar'],
    style: { backgroundColor: '#e3f2fd' } // light blue
  },
  {
    label: 'Temperature',
    units: ['°C', '°F'],
    style: { backgroundColor: '#fff3e0' } // light orange
  },
  {
    label: 'Length',
    units: ['mm', 'inch', 'km', 'mile'],
    style: { backgroundColor: '#f3e5f5' } // light purple
  },
  {
    label: 'Power',
    units: ['kW', 'hp'],
    style: { backgroundColor: '#e8f5e8' } // light green
  },
  {
    label: 'Torque',
    units: ['Nm', 'lb-ft'],
    style: { backgroundColor: '#fff8e1' } // light yellow
  },
  {
    label: 'Force Rate',
    units: ['N/mm', 'lb/in'],
    style: { backgroundColor: '#fce4ec' } // light pink
  },
  {
    label: 'Mass',
    units: ['kg', 'lb'],
    style: { backgroundColor: '#f1f8e9' } // light lime
  },
  {
    label: 'Volume',
    units: ['L', 'gal'],
    style: { backgroundColor: '#e0f2f1' } // light teal
  },
  {
    label: 'Speed',
    units: ['m/s', 'km/h', 'mph', 'ft/s'],
    style: { backgroundColor: '#ede7f6' } // light indigo
  },
  {
    label: 'Time',
    units: ['s', 'min', 'hr'],
    style: { backgroundColor: '#fafafa' } // light grey
  },
  {
    label: 'Fuel Efficiency',
    units: ['L/100km', 'mpg'],
    style: { backgroundColor: '#efebe9' } // light brown
  },
  {
    label: 'Angle',
    units: ['degrees'],
    style: { backgroundColor: '#f9fbe7' } // light yellow-green
  },
  {
    label: 'Other',
    units: ['%', 'rpm', 'kWh', 'V', 'A', 'W'],
    style: { backgroundColor: '#f5f5f5' } // light grey
  },
  {
    label: 'Unitless',
    units: [''],
    style: { fontWeight: 'bold', color: '#666' }
  },
  {
    label: 'Text',
    units: ['text'],
    style: { fontWeight: 'bold', color: '#007bff' } // bold blue
  }
];

// Legacy export for backward compatibility
const commonUnits = Object.values(unitCategories).flatMap(cat => cat.units);
export default commonUnits;

export { unitCategories, groupedUnits };